import { useState, useCallback } from 'react';

/**
 * Platform-agnostic hook for managing authentication form state.
 * Handles email, password, signup/login mode switching, and validation.
 */
export interface UseAuthFormResult {
  isSignup: boolean;
  email: string;
  password: string;
  passwordRepeat: string;
  passwordValid: boolean;
  canSubmit: boolean;
  setIsSignup: (value: boolean) => void;
  setEmail: (value: string) => void;
  setPassword: (value: string) => void;
  setPasswordRepeat: (value: string) => void;
  setPasswordValid: (value: boolean) => void;
  toggleMode: () => void;
  resetForm: () => void;
}

export function useAuthForm(defaultIsSignup = true): UseAuthFormResult {
  const [isSignup, setIsSignup] = useState(defaultIsSignup);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [passwordValid, setPasswordValid] = useState(false);

  // Toggle between login and signup mode, clearing sensitive fields
  const toggleMode = useCallback(() => {
    setIsSignup((prev) => !prev);
    setPassword('');
    setPasswordRepeat('');
    setPasswordValid(false);
  }, []);

  // Reset all form fields
  const resetForm = useCallback(() => {
    setEmail('');
    setPassword('');
    setPasswordRepeat('');
    setPasswordValid(false);
  }, []);

  // Check if form can be submitted
  const canSubmit = isSignup ? passwordValid : email.length > 0 && password.length > 0;

  return {
    isSignup,
    email,
    password,
    passwordRepeat,
    passwordValid,
    canSubmit,
    setIsSignup,
    setEmail,
    setPassword,
    setPasswordRepeat,
    setPasswordValid,
    toggleMode,
    resetForm,
  };
}
